import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-work-tabs>` says about itself.
 *
 * There are now three tab components and the difference between them is what a tab IS, not how it
 * looks:
 *
 *   TabStrip — a tab is a FACET of one thing. Details, Related, History. The set is authored and
 *   fixed; nobody opens a sixth facet of a record.
 *
 *   WorkspaceTabs — a tab is a DESTINATION in the app. The set comes from the app and is swapped
 *   wholesale when the app changes. It is part of the workspace frame and is not placed on a page.
 *
 *   WorkTabs — a tab is a PIECE OF WORK. A query, a record being edited, a request, a subscription
 *   being watched. The set is made by the person using it, at runtime, and everything about this
 *   component follows from that: tabs are opened and closed one at a time, they rename themselves
 *   from what they end up holding, and one of them can have unsaved work in it.
 *
 * The two parts most likely to be read as niceties are the two that matter most, so they live in
 * the component rather than in the destinations that use it. A tab takes its name from its content
 * the moment it has any — that is what lets somebody with six tabs open tell them apart without
 * clicking, which is the entire point of having six. And closing a tab with unsaved work in it asks
 * rather than discarding, here, once, so no destination can be the one that forgot.
 *
 * How many tabs a destination holds is an attribute with a documented default, never a number
 * written into a screen. At the cap the "new" control is disabled and says why — a developer tool
 * whose controls go quiet without explanation is the thing this application exists to be better
 * than.
 */
const docs: ComponentDocs = {
  aliases: ['open work', 'work tabs', 'editor tabs', 'document tabs', 'session tabs', 'query tabs'],

  answers: [
    'keep several queries open at once and move between them',
    'let somebody work on more than one thing in the same screen',
    'show which open tab has unsaved changes',
    'stop a person losing work by closing the wrong tab',
    'name a tab after whatever it ended up holding',
    'limit how many things can be open in one place',
  ],

  // A destination's own top strip. It is the frame of the screen it sits in, not something dropped
  // onto a record page beside a field — which is also why it is not granted the list surfaces.
  placements: ['appPage'],

  inputs: [
    {
      name: 'tabs',
      // No attribute: a list of open work cannot be written in one, and it changes as somebody works.
      type: 'json',
      description:
        'The open work, as { id, openedAs, namedBy?, dirty? } objects in the order they are drawn. ' +
        '`openedAs` is the name the tab was created with ("New Query 1"); `namedBy` is what its ' +
        'content calls it ("Account @10:38") and wins whenever it is set to anything but blank, so a ' +
        'tab renames itself as soon as it holds something and falls back rather than going nameless ' +
        'if that content is cleared. `dirty` draws the unsaved mark and makes closing ask first.',
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'text',
      description:
        'The id of the active tab, reflected as an attribute so it can be read back off the element. ' +
        'A value naming no open tab falls to the first one rather than leaving the strip with nothing ' +
        'lit, which would read as "nothing is open".',
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'number',
      default: 12,
      description:
        'How many pieces of work this destination may hold at once. At the cap the "new" control is ' +
        'disabled and explains itself, and the new event is not raised even by a programmatic click ' +
        '— the cap is a rule about how many tabs may exist, not a look the control wears. A value ' +
        'that is not a positive whole number is read as unset and falls back to the default, rather ' +
        'than being honoured as a cap of zero and making the destination unusable through a typo.',
    },
    {
      name: 'newLabel',
      attribute: 'new-label',
      type: 'text',
      default: 'New',
      description:
        'The word on the control that opens another piece of work. Worth setting to the noun the ' +
        'destination deals in — "New query", "New request" — so the control says what it will make.',
    },
  ],

  events: [
    {
      name: 'work-tab-select',
      when: 'A tab becomes active and was not already — by pointer, or by an arrow key, which moves and activates in one gesture.',
      carries: '{ id } — the id of the tab now active.',
    },
    {
      name: 'work-tab-new',
      when:
        'The "new" control was used. Never raised at the cap, so a listener can open a tab without ' +
        'counting first.',
      carries: 'nothing',
    },
    {
      name: 'work-tab-close',
      when:
        'A tab is to be closed. For a CLEAN tab this is immediate. For one with unsaved work it is ' +
        'raised only after the person has answered the question, so a listener can close on it ' +
        'directly and never has to ask again — by the time it arrives, somebody has said yes.',
      carries: '{ id } — the id of the tab to close.',
    },
  ],

  // Everything readable is also settable: `value` is documented as an input.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent', controls: 'The underline under the active tab, and the unsaved mark.' },
    { token: '--caos-color-border', controls: 'The rule the strip sits on, and the outline of the buttons in the question.' },
    { token: '--caos-color-surface', controls: 'The band the strip is drawn on.' },
    { token: '--caos-color-bg', controls: 'The ground the question about unsaved work sits on, and a hovered close mark.' },
    { token: '--caos-color-text', controls: 'The ink of the active tab and of a hovered one.' },
    { token: '--caos-color-text-muted', controls: 'The ink of the tabs that are not active, and of the new control.' },
    { token: '--caos-font-mono', controls: 'The typeface of the tab names and the new control.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a keyboard-focused tab or button.' },
    { token: '--caos-radius-sm', controls: 'The corners of the focus ring, the close mark and the question buttons.' },
  ],

  parts: [
    { part: 'tablist', is: 'the row of tabs' },
    { part: 'tab', is: 'one piece of open work' },
    { part: 'close', is: 'the mark that closes a tab' },
    { part: 'new', is: 'the control that opens another one' },
    { part: 'ask', is: 'the question asked before closing unsaved work' },
  ],

  methods: [],

  placing:
    'Across the full width at the very top of a destination, above the work it is holding. It draws ' +
    'its own rule underneath, so nothing needs to add a divider. Past the width available the tabs ' +
    'scroll sideways rather than wrapping, and a long content name truncates rather than pushing the ' +
    'other tabs out of sight — telling tabs apart is the point, and a strip scrolled out of view ' +
    'cannot do it. The question about unsaved work appears as a row directly under the strip rather ' +
    'than floating over it, because it is about one tab in a bar that scrolls and a popover pinned ' +
    'to a tab that scrolls away is a question with no visible subject.',

  whenToUse:
    'A screen where somebody genuinely works on several things at once and moving between them must ' +
    'not lose any of them — a query editor, an API console, a log viewer. Reach for it when the ' +
    'alternative is one pane that has to be emptied before the next thing can be started, which is ' +
    'the complaint the incumbent developer console draws.',

  insteadUse: [
    {
      name: 'TabStrip',
      when:
        'The tabs are FACETS of one thing rather than separate pieces of work — Details, Related, ' +
        'History. That set is authored and fixed, nothing is opened or closed, and nothing in it can ' +
        'hold unsaved work of its own.',
    },
    {
      name: 'WorkspaceTabs',
      when:
        'You are moving between destinations of an application rather than between things open ' +
        'inside one destination. That is part of the workspace frame and is not placed on a page.',
    },
    {
      name: 'SplitView',
      when:
        'Everything should be visible at once — a list beside the record it opens — rather than one ' +
        'thing at a time with the rest waiting in a strip.',
    },
  ],

  accessibility:
    'A WAI-ARIA tablist: `role="tablist"` around `role="tab"` buttons with `aria-selected` on the ' +
    'active one and a roving tabindex, so the whole strip is one Tab stop and Arrow ' +
    'Left/Right/Up/Down, Home and End move and activate. The close mark inside a tab is a span and ' +
    'not a button on purpose — a button is not a valid child of `role="tab"` — so the keyboard route ' +
    'to closing is Delete or Backspace on the focused tab, which is what the authoring practice for ' +
    'closable tabs prescribes. The unsaved mark carries the name "unsaved" rather than being colour ' +
    'alone. The question about closing unsaved work is an `alertdialog`, labelled by the sentence ' +
    'inside it so a screen reader says which tab has unsaved work, and it takes focus when it ' +
    'appears — on KEEPING it, because the safe answer is the one a second keystroke should reach. ' +
    'It is deliberately not `aria-modal`: it is a row under the strip and traps nothing, and the ' +
    'rest of the page is still there. Declining returns focus to the tab that was nearly lost, and ' +
    'closing one returns it to the tab the destination moved to rather than to nowhere. Left ' +
    'to the author: set `aria-label` saying what this destination holds — the default name is "Open ' +
    'work", which distinguishes nothing on a screen with two strips.',

  examples: [
    {
      key: 'several-queries',
      label: 'Several queries open at once',
      summary:
        'Three tabs: two that have run and named themselves from what they returned, and one still ' +
        'wearing the name it was opened with because it holds nothing yet.',
      placement: {
        component: 'caos-work-tabs',
        inputs: {
          'aria-label': 'Open queries',
          'new-label': 'New query',
          value: 'q2',
          tabs: [
            { id: 'q1', openedAs: 'New Query 1', namedBy: 'Account @10:38' },
            { id: 'q2', openedAs: 'New Query 2', namedBy: 'Opportunity @10:41' },
            { id: 'q3', openedAs: 'New Query 3' },
          ],
        },
      },
    },
    {
      key: 'unsaved',
      label: 'One of them has unsaved work',
      summary:
        'The second tab carries the unsaved mark. Closing that one puts the question on screen and ' +
        'raises nothing until it is answered; closing either of the others is immediate.',
      placement: {
        component: 'caos-work-tabs',
        inputs: {
          'aria-label': 'Open requests',
          'new-label': 'New request',
          value: 'r2',
          max: 6,
          tabs: [
            { id: 'r1', openedAs: 'New request 1', namedBy: 'GET /accounts' },
            { id: 'r2', openedAs: 'New request 2', namedBy: 'POST /orders', dirty: true },
            { id: 'r3', openedAs: 'New request 3' },
          ],
        },
      },
    },
  ],
};

export default docs;
